/* Visualization-project upload helper.
 *
 * Self-contained: no Drive, no LAS/LAZ allowlist, no `googleDriveId` field,
 * no shared imports from uploadStrategies.js.
 *
 * Two paths, chosen by size:
 *   < 50 MB  single multipart-form POST to /upload/visualization. Simple, one
 *            round trip, but the whole body is buffered by Waitress and then
 *            read into memory server-side — which is why it is capped at the
 *            backend's 2 GB MAX_CONTENT_LENGTH and cannot resume.
 *   >= 50 MB S3 multipart via /upload/visualization-init, -presign, -complete.
 *            Parts go straight from the browser to the project bucket with
 *            presigned URLs, so there is no size ceiling, no server buffering,
 *            and a failed part retries on its own instead of restarting the
 *            whole file.
 *
 * Do NOT add branches here for rsa-existing / rsa-model — they get their
 * own helpers. Cross-type branching is the bug class this split exists to
 * prevent. In particular do not reuse /upload/init-multipart + complete-large:
 * those enforce upload-prefix conventions and carry Drive/LAS/reprojection
 * behaviour that visualization projects do not have.
 */

export const VISUALIZATION_MULTIPART_THRESHOLD = 50 * 1024 * 1024; // 50 MB

// Presign in batches rather than all at once: presigned URLs expire after an
// hour, and a multi-hundred-part upload can outlive that.
const PRESIGN_BATCH_SIZE = 10;
// Parts in flight at once. Enough to saturate a normal connection without
// opening so many sockets that progress reporting gets choppy.
const PART_CONCURRENCY = 3;

function buildUrl(api, suffix) {
  return `${api.apiBaseUrl}/api/projects/${api.projectId}/upload/${suffix}`;
}

async function postJson(api, suffix, body) {
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (api.authToken) headers.Authorization = `Bearer ${api.authToken}`;

  const response = await fetch(buildUrl(api, suffix), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
  });

  let payload = null;
  try { payload = await response.json(); } catch (_e) { /* keep null */ }
  if (!response.ok) {
    const msg = (payload && payload.error) || `${suffix} failed (${response.status})`;
    throw new Error(msg);
  }
  return payload || {};
}

/** PUT one part straight to S3. Resolves with the part's ETag. */
function putPart({ url, blob, onPartProgress, registerXhr, unregisterXhr }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (registerXhr) registerXhr(xhr);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onPartProgress) onPartProgress(e.loaded);
    });

    xhr.onload = () => {
      if (unregisterXhr) unregisterXhr(xhr);
      if (xhr.status >= 200 && xhr.status < 300) {
        // S3 returns the part ETag in a header; without it the upload cannot
        // be completed. Requires ETag to be exposed via bucket CORS.
        const etag = xhr.getResponseHeader('ETag');
        if (!etag) {
          reject(new Error('S3 did not return an ETag for the uploaded part'));
          return;
        }
        if (onPartProgress) onPartProgress(blob.size);
        resolve(etag);
      } else {
        reject(new Error(`Part upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => {
      if (unregisterXhr) unregisterXhr(xhr);
      reject(new Error('Network error during part upload'));
    };
    xhr.onabort = () => {
      if (unregisterXhr) unregisterXhr(xhr);
      reject(new Error('Upload cancelled'));
    };

    xhr.open('PUT', url);
    xhr.send(blob);
  });
}

async function uploadVisualizationMultipart({
  file, parentId, onProgress, registerXhr, unregisterXhr, api,
}) {
  const session = await postJson(api, 'visualization-init', {
    filename: file.name,
    fileSize: file.size,
    mimeType: file.type || 'application/octet-stream',
    parentId: String(parentId),
  });

  const { s3UploadId, s3Key, partSize, numParts } = session;
  if (!s3UploadId || !s3Key || !partSize || !numParts) {
    throw new Error('visualization-init returned an incomplete session');
  }

  // Per-part byte counts, summed for overall progress. Held separately so a
  // retried part overwrites its own contribution rather than double-counting.
  const loadedByPart = new Map();
  const reportProgress = () => {
    if (!onProgress) return;
    let loaded = 0;
    loadedByPart.forEach((n) => { loaded += n; });
    onProgress({ loadedBytes: Math.min(loaded, file.size), totalBytes: file.size });
  };

  const parts = [];
  for (let start = 1; start <= numParts; start += PRESIGN_BATCH_SIZE) {
    const batch = [];
    for (let n = start; n < start + PRESIGN_BATCH_SIZE && n <= numParts; n += 1) {
      batch.push(n);
    }

    const { presignedUrls } = await postJson(api, 'visualization-presign', {
      s3Key, s3UploadId, partNumbers: batch,
    });

    for (let i = 0; i < batch.length; i += PART_CONCURRENCY) {
      const slice = batch.slice(i, i + PART_CONCURRENCY);
      const settled = await Promise.all(slice.map(async (partNumber) => {
        const url = presignedUrls && presignedUrls[String(partNumber)];
        if (!url) throw new Error(`No presigned URL for part ${partNumber}`);

        const from = (partNumber - 1) * partSize;
        const blob = file.slice(from, Math.min(from + partSize, file.size));
        const etag = await putPart({
          url,
          blob,
          onPartProgress: (loaded) => {
            loadedByPart.set(partNumber, loaded);
            reportProgress();
          },
          registerXhr,
          unregisterXhr,
        });
        return { PartNumber: partNumber, ETag: etag };
      }));
      parts.push(...settled);
    }
  }

  return postJson(api, 'visualization-complete', {
    s3Key,
    s3UploadId,
    parts,
    filename: file.name,
    parentId: String(parentId),
  });
}

function uploadVisualizationSinglePost({
  file, parentId, onProgress, registerXhr, unregisterXhr, api,
}) {
  const url = buildUrl(api, 'visualization');
  const form = new FormData();
  form.append('file', file);
  form.append('parent_id', String(parentId));

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (registerXhr) registerXhr(xhr);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress({ loadedBytes: e.loaded, totalBytes: e.total || file.size });
      }
    });

    xhr.onload = () => {
      if (unregisterXhr) unregisterXhr(xhr);
      let payload = null;
      try { payload = JSON.parse(xhr.responseText); } catch (_e) { /* keep null */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload || { success: true });
      } else {
        const msg = (payload && payload.error)
          || `Visualization upload failed (${xhr.status})`;
        reject(new Error(msg));
      }
    };

    xhr.onerror = () => {
      if (unregisterXhr) unregisterXhr(xhr);
      reject(new Error('Network error during visualization upload'));
    };

    xhr.onabort = () => {
      if (unregisterXhr) unregisterXhr(xhr);
      reject(new Error('Upload cancelled'));
    };

    xhr.open('POST', url);
    xhr.withCredentials = true;
    if (api.authToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${api.authToken}`);
    }
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.send(form);
  });
}

export async function uploadVisualizationFile(options) {
  const { file } = options;
  if (file && file.size >= VISUALIZATION_MULTIPART_THRESHOLD) {
    return uploadVisualizationMultipart(options);
  }
  return uploadVisualizationSinglePost(options);
}
